/**
 * 购物车折扣码自动应用系统
 * 
 * 功能：
 * - 自动拦截 /cart/add.js 请求
 * - 应用产品关联的折扣码
 * - 支持多折扣码累积
 * - 防止竞态条件
 * - 折扣码验证
 * 
 * @version 1.0.0
 */

(function() {
  'use strict';

  // ========== 🔧 开发调试配置 ==========
  const DEV_CONFIG = {
    // 开发模式开关（生产环境请设置为 false）
    isDev: true,
    
    // 折扣码应用方式：
    // 'add' - 在 /cart/add.js 请求中直接带上折扣码（❌ Shopify 官方不支持此参数）
    // 'update' - 添加后再调用 /cart/update.js 应用折扣码（✅ Shopify 官方支持，推荐使用）
    applyMethod: 'update',  // 可选值: 'add' | 'update'
    
    // 调试日志级别：
    // 'verbose' - 显示所有日志（包括启动信息、详细流程）
    // 'normal'  - 显示重要信息（推荐开发时使用，隐藏启动日志）
    // 'error'   - 仅显示错误
    // 'off'     - 关闭所有日志（生产环境）
    logLevel: 'verbose'  // 生产环境：关闭所有日志
  };
  
  // 运行时配置（根据开发模式自动调整）
  const CONFIG = {
    applyMethod: DEV_CONFIG.applyMethod,
    debug: DEV_CONFIG.isDev && DEV_CONFIG.logLevel !== 'off'
  };
  
  const STORAGE_KEY = 'product_discount_codes';
  const CART_DRAWER_SELECTORS = [
    '[id*="cart-drawer"]',
    '#shopify-section-cart-drawer',
    'cart-drawer',
    '.cart-drawer'
  ];
  
  // ========== 📝 调试日志系统 ==========
  const logger = {
    verbose: (...args) => {
      if (DEV_CONFIG.isDev && ['verbose'].includes(DEV_CONFIG.logLevel)) {
        console.log('🔍 [VERBOSE]', ...args);
      }
    },
    
    info: (...args) => {
      if (DEV_CONFIG.isDev && ['verbose', 'normal'].includes(DEV_CONFIG.logLevel)) {
        console.log('ℹ️ [INFO]', ...args);
      }
    },
    
    success: (...args) => {
      if (DEV_CONFIG.isDev && ['verbose', 'normal'].includes(DEV_CONFIG.logLevel)) {
        console.log('✅ [SUCCESS]', ...args);
      }
    },
    
    warn: (...args) => {
      if (DEV_CONFIG.isDev && ['verbose', 'normal', 'error'].includes(DEV_CONFIG.logLevel)) {
        console.warn('⚠️ [WARN]', ...args);
      }
    },
    
    error: (...args) => {
      if (DEV_CONFIG.isDev) {
        console.error('❌ [ERROR]', ...args);
      }
    }
  };
  
  // ========== 📦 存储管理 ==========
  
  /**
   * 获取所有产品的折扣码映射
   * @returns {Object} { productId: discountCode }
   */
  function getProductDiscountMap() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      logger.error('读取产品折扣码映射失败:', error);
      return {};
    }
  }

  /**
   * 保存产品的折扣码
   * @param {string|number} productId - 产品 ID
   * @param {string} discountCode - 折扣码
   */
  function saveProductDiscountCode(productId, discountCode) {
    if (!productId || !discountCode) return {};
    
    try {
      const discountMap = getProductDiscountMap();
      discountMap[productId] = discountCode;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(discountMap));
      logger.info('产品折扣码已保存:', { productId, discountCode });
      return discountMap;
    } catch (error) {
      logger.error('保存产品折扣码失败:', error);
      return { [productId]: discountCode };
    }
  }

  /**
   * 获取购物车中所有产品的折扣码（去重）
   * @returns {string[]}
   */
  function getCartDiscountCodes() {
    try {
      const discountMap = getProductDiscountMap();
      const codes = Object.values(discountMap);
      return [...new Set(codes)];
    } catch (error) {
      logger.error('获取购物车折扣码失败:', error);
      return [];
    }
  }

  /**
   * 获取所有折扣码的字符串（逗号分隔）
   * @returns {string}
   */
  function getAllDiscountCodesString() {
    const codes = getCartDiscountCodes();
    return codes.join(',');
  }

  /**
   * 从购物车中移除产品时，清除对应的折扣码
   * @param {string|number} productId - 产品 ID
   */
  function removeProductDiscountCode(productId) {
    if (!productId) return;
    
    try {
      const discountMap = getProductDiscountMap();
      delete discountMap[productId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(discountMap));
      logger.info('产品折扣码已移除:', productId);
    } catch (error) {
      logger.error('移除产品折扣码失败:', error);
    }
  }

  // ========== 🎨 UI 更新 ==========
  
  /**
   * 更新购物车 UI
   * @param {Object} data - 购物车数据
   */
  function updateCartUI(data) {
    if (!data.sections) {
      logger.verbose('⚠️ 响应中没有 sections 数据，跳过 UI 更新');
      return;
    }
    
    // 更新 cart-icon-bubble
    if (data.sections['cart-icon-bubble']) {
      const cartIconElement = document.getElementById('shopify-section-cart-icon-bubble');
      if (cartIconElement) {
        cartIconElement.innerHTML = data.sections['cart-icon-bubble'];
        logger.success('✅ cart-icon-bubble UI 已更新');
      }
    }
    
    // 触发主题的 cart:refresh 事件来刷新 cart-drawer
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    logger.success('✅ 已触发 cart:refresh 事件');
  }

  // ========== 🔄 请求管理（防止竞态条件）==========
  
  let activeDiscountRequest = null;
  
  /**
   * 创建 AbortController（取消之前的请求）
   * @returns {AbortController}
   */
  function createAbortController() {
    if (activeDiscountRequest) {
      activeDiscountRequest.abort();
      logger.verbose('⚠️ 取消之前的折扣码请求');
    }
    const abortController = new AbortController();
    activeDiscountRequest = abortController;
    return abortController;
  }
  
  // ========== 💳 折扣码应用 ==========
  
  /**
   * 应用折扣码到购物车
   * @param {string} newDiscountCode - 新的折扣码
   * @param {string|number} productId - 产品 ID
   */
  async function applyDiscountToCart(newDiscountCode, productId) {
    if (!newDiscountCode) return;
    
    const abortController = createAbortController();
    
    try {
      saveProductDiscountCode(productId, newDiscountCode);
      
      const allCodes = getCartDiscountCodes();
      
      // 检查折扣码是否已存在（避免重复）
      if (allCodes.filter(code => code === newDiscountCode).length > 1) {
        logger.warn('⚠️ 折扣码已存在，跳过重复添加:', newDiscountCode);
        return;
      }
      
      const discountCodesString = allCodes.join(',');
      
      logger.info('💳 通过 update 应用折扣码:', discountCodesString, '| 产品:', productId);
      
      const requestUrl = window.Shopify.routes.root + 'cart/update.js';
      const requestBody = {
        discount: discountCodesString,
        sections: 'cart-drawer,cart-icon-bubble'
      };
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: abortController.signal
      });
      
      if (response.ok) {
        const data = await response.json();
        
        updateCartUI(data);
        
        // 折扣码验证
        validateDiscountCodes(discountCodesString, data, allCodes);
        
        // 保存到 sessionStorage 作为备份
        sessionStorage.setItem('pending_discount_codes', discountCodesString);
        
        // 触发自定义事件
        document.dispatchEvent(new CustomEvent('cart:discount-applied', {
          detail: { 
            discountCodes: discountCodesString,
            discountCodesArray: allCodes,
            cartData: data 
          }
        }));
      } else {
        const errorData = await response.json();
        logger.error('❌ 折扣码应用失败:', response.status, errorData);
        sessionStorage.setItem('pending_discount_codes', discountCodesString);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        logger.verbose('⚠️ 折扣码请求已取消');
        return;
      }
      
      logger.error('❌ 折扣码应用异常:', error.message);
      const allCodesString = getAllDiscountCodesString();
      sessionStorage.setItem('pending_discount_codes', allCodesString);
    } finally {
      if (activeDiscountRequest === abortController) {
        activeDiscountRequest = null;
      }
    }
  }

  /**
   * 验证折扣码（参考 Shopify 官方实现）
   * @param {string} discountCodesString - 折扣码字符串
   * @param {Object} data - 购物车数据
   * @param {string[]} allCodes - 所有折扣码数组
   */
  function validateDiscountCodes(discountCodesString, data, allCodes) {
    if (!discountCodesString) return;
    
    // 检查购物车数据中的折扣信息
    const hasDiscountCodes = data.discount_codes && data.discount_codes.length > 0;
    const hasDiscount = data.total_discount && data.total_discount > 0;
    const hasDiscountApplications = 
      (data.discount_applications && data.discount_applications.length > 0) ||
      (data.cart_level_discount_applications && data.cart_level_discount_applications.length > 0);
    
    logger.verbose('🔍 购物车折扣数据:', {
      discount_codes: data.discount_codes,
      total_discount: data.total_discount,
      discount_applications: data.discount_applications,
      cart_level_discount_applications: data.cart_level_discount_applications
    });
    
    // 优先检查是否有实际折扣金额
    if (hasDiscount) {
      logger.success(`✅ 折扣已成功应用，折扣金额: ${(data.total_discount / 100).toFixed(2)}`);
      
      // 如果有 discount_codes 数据，显示详细信息
      if (hasDiscountCodes) {
        const invalidCodes = [];
        const validCodes = [];
        
        allCodes.forEach(code => {
          const discountInfo = data.discount_codes.find(d => d.code === code);
          
          if (discountInfo) {
            if (discountInfo.applicable === false) {
              invalidCodes.push(code);
            } else {
              validCodes.push(code);
            }
          }
        });
        
        if (validCodes.length > 0) {
          logger.info(`📋 有效折扣码: ${validCodes.join(', ')}`);
        }
        if (invalidCodes.length > 0) {
          logger.warn(`⚠️ 无效折扣码: ${invalidCodes.join(', ')}`);
        }
      }
    } else if (hasDiscountApplications) {
      // 有折扣应用但没有折扣金额（可能是运费折扣）
      logger.success('✅ 折扣已应用（可能是运费折扣或其他类型）');
    } else if (hasDiscountCodes) {
      // 有折扣码数据但没有折扣金额
      logger.warn('⚠️ 折扣码已添加，但可能需要满足特定条件才能生效');
    } else {
      // 没有任何折扣信息
      logger.warn('⚠️ 折扣码已提交，等待验证...');
    }
  }

  // ========== 🎯 事件监听 ==========

  /**
   * 监听 variant:add 事件，打印当前购物车价格
   */
  document.addEventListener('variant:add', async (event) => {
    logger.info('📦 检测到商品添加事件');

    // 延迟执行，确保购物车已更新
    setTimeout(async () => {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cartData = await response.json();

        logger.info('💰 当前购物车总价:', (cartData.total_price / 100).toFixed(2), cartData.currency);
        logger.info('🛒 商品数量:', cartData.item_count);
        logger.info('💸 折扣金额:', (cartData.total_discount / 100).toFixed(2), cartData.currency);
      } catch (error) {
        logger.error('❌ 获取购物车数据失败:', error);
      }
    }, 500);
  });

  /**
   * 监听结账按钮，应用所有累积的折扣码
   */
  document.addEventListener('click', function(e) {
    const target = e.target.closest('a[href*="/checkout"], button[name="checkout"]');
    if (target) {
      const allDiscountCodes = getAllDiscountCodesString() || sessionStorage.getItem('pending_discount_codes');

      if (allDiscountCodes) {
        e.preventDefault();
        logger.info('结账时应用折扣码:', allDiscountCodes);
        window.location.href = `/checkout?discount=${encodeURIComponent(allDiscountCodes)}`;
      }
    }
  }, true);

  // ========== 🔌 Fetch 拦截器 ==========
  
  const originalFetch = window.fetch;
  const fetchInterceptorId = `discount-cart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const previousFetchInterceptorId = originalFetch.__aestDiscountCartInterceptorId || null;
  let cartAddRequestSequence = 0;

  function getCartAddBodySummary(options) {
    const body = options?.body;

    if (!body) {
      return { bodyType: 'none' };
    }

    if (body instanceof FormData) {
      return {
        bodyType: 'FormData',
        variantId: body.get('id'),
        productId: body.get('product-id'),
        sections: body.get('sections')
      };
    }

    if (typeof body === 'string') {
      try {
        const parsedBody = JSON.parse(body);
        return {
          bodyType: 'JSON string',
          variantId: parsedBody.id,
          itemCount: Array.isArray(parsedBody.items) ? parsedBody.items.length : undefined,
          itemVariantIds: Array.isArray(parsedBody.items)
            ? parsedBody.items.map((item) => item.id)
            : undefined,
          sections: parsedBody.sections
        };
      } catch (error) {
        return { bodyType: 'string (non-JSON)' };
      }
    }

    return { bodyType: body.constructor?.name || typeof body };
  }

  console.log('[AEST cart/add debug] fetch interceptor installed', {
    interceptorId: fetchInterceptorId,
    previousFetchInterceptorId,
    installedAt: new Date().toISOString(),
    originalFetchName: originalFetch.name
  });
  
  window.fetch = function(...args) {
    let [url, options] = args;
    const requestUrl = typeof url === 'string' ? url : url?.url;
    
    // 检测是否是添加到购物车的请求
    if (requestUrl && (requestUrl.includes('/cart/add') || requestUrl.includes('/cart/add.js'))) {
      cartAddRequestSequence += 1;
      const debugRequestId = `${fetchInterceptorId}#${cartAddRequestSequence}`;
      const requestStartedAt = performance.now();

      console.log('[AEST cart/add debug] intercepted', {
        debugRequestId,
        interceptorId: fetchInterceptorId,
        previousFetchInterceptorId,
        timestamp: new Date().toISOString(),
        url: requestUrl,
        method: options?.method || url?.method || 'GET',
        body: getCartAddBodySummary(options),
        callerStack: new Error('cart/add fetch caller').stack
      });
      
      // 方式 A: 在 add 请求中直接带上折扣码（不推荐，官方不支持）
      if (CONFIG.applyMethod === 'add') {
        const tempDiscount = sessionStorage.getItem('temp_discount_code');
        
        if (tempDiscount && options && options.body) {
          const allCodes = getCartDiscountCodes();
          if (!allCodes.includes(tempDiscount)) {
            allCodes.push(tempDiscount);
          }
          const discountCodesString = allCodes.join(',');
          
          logger.verbose('💳 在 add 请求中应用折扣码:', discountCodesString);
          
          try {
            let bodyData;
            
            if (typeof options.body === 'string') {
              try {
                bodyData = JSON.parse(options.body);
              } catch (e) {
                logger.verbose('⚠️ body 不是 JSON 格式，跳过修改');
                bodyData = null;
              }
            } else if (options.body instanceof FormData) {
              bodyData = {};
              for (let [key, value] of options.body.entries()) {
                bodyData[key] = value;
              }
            } else {
              bodyData = options.body;
            }
            
            if (bodyData && typeof bodyData === 'object') {
              bodyData.discount = discountCodesString;
              bodyData.sections = 'cart-drawer,cart-icon-bubble';
              
              options = {
                ...options,
                body: JSON.stringify(bodyData),
                headers: {
                  ...options.headers,
                  'Content-Type': 'application/json'
                }
              };
              
              args = [url, options];
              logger.success('✅ 请求已修改，添加折扣码参数');
            }
          } catch (e) {
            logger.warn('⚠️ 修改请求失败:', e.message);
          }
        }
      }
      
      // 执行请求并处理响应
      let originalFetchPromise;

      try {
        console.log('[AEST cart/add debug] dispatching original fetch', {
          debugRequestId,
          timestamp: new Date().toISOString()
        });
        originalFetchPromise = originalFetch.apply(this, args);
      } catch (error) {
        console.log('[AEST cart/add debug] original fetch threw synchronously', {
          debugRequestId,
          elapsedMs: Math.round(performance.now() - requestStartedAt),
          error
        });
        throw error;
      }

      return originalFetchPromise.then(async (response) => {
        const clonedResponse = response.clone();

        console.log('[AEST cart/add debug] response received', {
          debugRequestId,
          timestamp: new Date().toISOString(),
          elapsedMs: Math.round(performance.now() - requestStartedAt),
          status: response.status,
          ok: response.ok,
          responseUrl: response.url
        });
        
        if (response.ok) {
          try {
            const responseData = await response.clone().json();
            let productId = null;
            let discountCode = sessionStorage.getItem('temp_discount_code');

            console.log('[AEST cart/add debug] response parsed', {
              debugRequestId,
              itemCount: Array.isArray(responseData.items) ? responseData.items.length : undefined,
              itemVariantIds: Array.isArray(responseData.items)
                ? responseData.items.map((item) => item.variant_id || item.id)
                : responseData.variant_id ? [responseData.variant_id] : undefined,
              productIds: Array.isArray(responseData.items)
                ? responseData.items.map((item) => item.product_id)
                : responseData.product_id ? [responseData.product_id] : undefined
            });
            
            // 获取产品 ID
            if (responseData.items && responseData.items.length > 0) {
              productId = responseData.items[0].product_id;
            } else if (responseData.product_id) {
              productId = responseData.product_id;
            }
            
            if (discountCode && productId) {
              saveProductDiscountCode(productId, discountCode);
              sessionStorage.removeItem('temp_discount_code');
              
              // 方式 B: 添加后再调用 update 应用折扣码（推荐）
              if (CONFIG.applyMethod === 'update') {
                logger.info('💳 检测到折扣码:', discountCode, '| 产品:', productId);
                
                // 延迟执行，等待主题完成初始的 cart-drawer 更新
                setTimeout(async () => {
                  await applyDiscountToCart(discountCode, productId);
                }, 300);
              } else {
                logger.success('✅ 折扣码已在 add 请求中应用');
                
                if (responseData.sections) {
                  updateCartUI(responseData);
                }
              }
            }
          } catch (error) {
            logger.error('❌ 处理购物车响应失败:', error);
          }
        }
        
        console.log('[AEST cart/add debug] returning cloned response', {
          debugRequestId,
          elapsedMs: Math.round(performance.now() - requestStartedAt)
        });
        return clonedResponse;
      }, (error) => {
        console.log('[AEST cart/add debug] original fetch rejected', {
          debugRequestId,
          elapsedMs: Math.round(performance.now() - requestStartedAt),
          error
        });
        throw error;
      });
    }
    
    // 其他请求正常处理
    return originalFetch.apply(this, args);
  };

  window.fetch.__aestDiscountCartInterceptorId = fetchInterceptorId;

  // ========== 🌐 全局 API ==========
  
  /**
   * 清空所有折扣码
   */
  window.clearDiscountCodes = function() {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('pending_discount_codes');
    logger.info('所有产品折扣码已清空');
    
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ discount: '' })
    }).then(() => {
      logger.info('购物车折扣码已清空');
    });
  };

  /**
   * 查看当前折扣码
   */
  window.getDiscountCodes = function() {
    const discountMap = getProductDiscountMap();
    const codes = getCartDiscountCodes();
    console.log('产品折扣码映射:', discountMap);
    console.log('当前累积的折扣码:', codes);
    return { discountMap, codes };
  };

  /**
   * 移除特定产品的折扣码
   */
  window.removeProductDiscount = function(productId) {
    removeProductDiscountCode(productId);
    logger.info('已移除产品折扣码:', productId);
  };

  /**
   * 切换折扣码应用方式
   */
  window.setDiscountApplyMethod = function(method) {
    if (method === 'add' || method === 'update') {
      CONFIG.applyMethod = method;
      logger.info('✅ 折扣码应用方式已切换为:', method);
      logger.info('  - add: 在添加到购物车时直接应用（❌ 官方不支持）');
      logger.info('  - update: 添加后再调用 update 应用（✅ 官方支持）');
    } else {
      logger.error('❌ 无效的方式，请使用 "add" 或 "update"');
    }
  };

  /**
   * 切换调试模式
   */
  window.toggleDiscountDebug = function(enabled) {
    CONFIG.debug = enabled !== false;
    logger.info('✅ 调试模式:', CONFIG.debug ? '开启' : '关闭');
  };

  /**
   * 查看完整的购物车数据（用于调试）
   */
  window.debugCartData = function() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cartData => {
        console.log('═══════════════════════════════════════');
        console.log('🛒 完整购物车数据');
        console.log('═══════════════════════════════════════');
        console.log('�扣 总价:', (cartData.total_price / 100).toFixed(2), cartData.currency || 'USD');
        console.log('💸 折扣金额:', (cartData.total_discount / 100).toFixed(2), cartData.currency || 'USD');
        console.log('� 商品:数量:', cartData.item_count);
        console.log('───────────────────────────────────────');
        console.log('🎫 折扣码:', cartData.discount_codes);
        console.log('📋 折扣应用:', cartData.discount_applications);
        console.log('📋 购物车级折扣:', cartData.cart_level_discount_applications);
        console.log('───────────────────────────────────────');
        console.log('📄 完整数据:', cartData);
        console.log('═══════════════════════════════════════');
        
        // 返回数据供进一步使用
        return cartData;
      })
      .catch(error => {
        console.error('❌ 获取购物车数据失败:', error);
      });
  };

  // ========== 🚀 初始化 ==========
  
  // 仅在 verbose 模式下显示启动信息
  if (DEV_CONFIG.logLevel === 'verbose') {
    logger.info('� 购物车折扣码系码统已启动');
    logger.verbose('📋 当前配置:', CONFIG);
    logger.verbose('💾 当前产品折扣码映射:', getProductDiscountMap());
  }
  
})();
